import { useMemo } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { standardFacetNames } from '../../../state/filters';
import { ApiFacetLoop54, Attribute } from '../../../types';
import Checkbox from '../../Form/Checkbox/Checkbox';
import { toAlphaNumeric } from '../../utils';
import './FilterPanelContent.css';
import { ExpandableSection, FilterSection } from './FilterSection/FilterSection';

type FilterPanelContentProps = {
	filters: ApiFacetLoop54[];
	onFilterChange: (filterName: string, itemValue: string | [number, number], isApplied: boolean) => void;
	limitedCustomFilters?: string[];
	sortOverride?: Attribute[];
};

export const FilterPanelContent = ({
	filters,
	onFilterChange,
	limitedCustomFilters,
	sortOverride,
}: FilterPanelContentProps) => {
	const t = useTranslation().filtering;

	const attributeMap = useMemo(() => {
		if (!sortOverride) return new Map<string, Attribute>();
		return new Map(sortOverride.map((item) => [item.attribute.toString(), item]));
	}, [sortOverride]);

	const getDescription = (filterName: string): string | null | undefined => {
		return attributeMap.get(filterName.split('_')[0])?.description;
	};

	// separate standard filters and remaining filters
	const { standardFilters, remainingFilters } = filters.reduce(
		(acc, filter) => {
			if (filter.name in standardFacetNames && filter.type === 'distinct' && filter.items) {
				// only use items where item.item === true (we only want the standard filters to be filterable on e.g. inStock=true etc., not the opposite)
				const trueItems = filter.items.filter((item) => item.item);
				if (trueItems.length > 0) {
					acc.standardFilters.push({
						...filter,
						items: trueItems,
						displayName: standardFacetNames[filter.name],
					});
				}
			} else {
				acc.remainingFilters.push(filter);
			}
			return acc;
		},
		{ standardFilters: [] as ApiFacetLoop54[], remainingFilters: [] as ApiFacetLoop54[] }
	);

	const fixedFacetNames = ['price', 'brand', 'bottomCategory'];

	// Sort remainingFilters based on sortOverride if available
	const sortedRemainingFilters =
		sortOverride && sortOverride.length > 0
			? (() => {
					// Separate filters by sort value
					const filtersWithSort = remainingFilters.filter((filter) => {
						const attributeNumber = filter.name.split('_')[0];
						const sortItem = sortOverride.find((item) => attributeNumber === item.attribute.toString());
						return sortItem && sortItem.sort > 0;
					});

					const filtersWithSortZero = remainingFilters.filter((filter) => {
						const attributeNumber = filter.name.split('_')[0];
						const sortItem = sortOverride.find((item) => attributeNumber === item.attribute.toString());
						return sortItem && sortItem.sort === 0;
					});

					// Sort filters with sort > 0 by sort number (ascending)
					const sortedFiltersWithSort = filtersWithSort.sort((a, b) => {
						const aSortItem = sortOverride.find((item) => {
							const attributeNumber = a.name.split('_')[0];
							return attributeNumber === item.attribute.toString();
						});
						const bSortItem = sortOverride.find((item) => {
							const attributeNumber = b.name.split('_')[0];
							return attributeNumber === item.attribute.toString();
						});

						if (aSortItem && bSortItem) {
							return aSortItem.sort - bSortItem.sort;
						}
						return 0;
					});

					// Sort filters with sort = 0 by id (ascending)
					const sortedFiltersWithSortZero = filtersWithSortZero.sort((a, b) => {
						const aSortItem = sortOverride.find((item) => {
							const attributeNumber = a.name.split('_')[0];
							return attributeNumber === item.attribute.toString();
						});
						const bSortItem = sortOverride.find((item) => {
							const attributeNumber = b.name.split('_')[0];
							return attributeNumber === item.attribute.toString();
						});

						if (aSortItem && bSortItem) {
							return aSortItem.id - bSortItem.id;
						}
						return 0;
					});

					const sortedFilters = [...sortedFiltersWithSort, ...sortedFiltersWithSortZero];

					// Preserve only fixed facets (price/brand/bottomCategory) that are not part of sortOverride
					// Exclude custom attribute facets that don't match sortOverride
					const unmatchedFilters = remainingFilters.filter((filter) => {
						// Don't include filters already in sortedFilters
						if (sortedFilters.includes(filter)) return false;

						// Always include fixed facets (price, brand, bottomCategory)
						if (fixedFacetNames.includes(filter.name)) return true;

						// Exclude custom attribute facets that aren't in sortOverride
						return false;
					});

					// Combine: first filters with sort > 0, then filters with sort = 0, then any unmatched fixed facets
					return [...sortedFilters, ...unmatchedFilters];
				})()
			: sortOverride !== undefined
				? // Category has no linked attributes — only show fixed facets, hide Loop54 auto-discovered ones
					remainingFilters.filter((f) => fixedFacetNames.includes(f.name))
				: remainingFilters;

	const fixedTopFacetNames = ['price', 'brand'];
	const fixedTopFacetPriority: Record<string, number> = { price: 0, brand: 1 };
	const topFixedFilters = sortedRemainingFilters.filter((filter) => fixedTopFacetNames.includes(filter.name));
	const otherFilters = sortedRemainingFilters.filter((filter) => !fixedTopFacetNames.includes(filter.name));

	const StandardFilters = () => (
		<ExpandableSection heading={t.showOnly}>
			<ul className="filter-items-list">
				{standardFilters.map((filter) =>
					filter.type === 'distinct' && filter.items
						? filter.items.map((item, i) => (
								<li key={item.item + i}>
									<div className="filter-item">
										<Checkbox
											id={toAlphaNumeric(String(item.item))}
											label={filter.displayName ?? filter.name}
											checked={item.selected}
											onChange={() => onFilterChange(filter.name, item.item, !item.selected)}
										/>
										{item.count > 0 && <span className="filter-item-count">({item.count})</span>}
									</div>
								</li>
							))
						: null
				)}
			</ul>
		</ExpandableSection>
	);

	const shouldRenderFilter = (filter: ApiFacetLoop54) =>
		limitedCustomFilters?.length === 0 ||
		limitedCustomFilters?.includes(filter.name) ||
		fixedTopFacetNames.includes(filter.name);

	return (
		<div id="filter-panel-content">
			{standardFilters.length > 0 && <StandardFilters />}
			{topFixedFilters
				.sort(
					(a, b) =>
						(fixedTopFacetPriority[a.name] ?? Number.MAX_SAFE_INTEGER) -
						(fixedTopFacetPriority[b.name] ?? Number.MAX_SAFE_INTEGER)
				)
				.map((filter, index) => {
					if (shouldRenderFilter(filter)) {
						return (
							<FilterSection
								key={index}
								filter={filter}
								onFilterChange={onFilterChange}
								description={getDescription(filter.name)}
							/>
						);
					} else {
						return null;
					}
				})}
			{otherFilters.map((filter, index) => {
				if (shouldRenderFilter(filter)) {
					return (
						<FilterSection
							key={index}
							filter={filter}
							onFilterChange={onFilterChange}
							description={getDescription(filter.name)}
						/>
					);
				} else {
					return null;
				}
			})}
		</div>
	);
};
